import type { BrandMediaItem } from '../api/brandMedia.api';

/**
 * Signed urls by asset id, typed by shape rather than imported from the asset entity, which is a
 * sibling this one may not reach for.
 */
type ResolvedUrls = Readonly<Record<string, string>>;

/** One offerable piece of media, flattened to what a thumbnail needs. */
export interface MediaTile {
  id: string;
  assetId: string;
  title: string;
  /**
   * What this tile would bind.
   *
   * Optional because a caller may prepend a tile for an asset bound under another brief or market,
   * whose library row is not in the answer, so its kind is genuinely unknown rather than defaulted.
   */
  mediaType?: 'image' | 'video';
  description?: string;
  /** Undefined until the signed url resolves. A tile draws no image until it does. */
  url?: string;
  /**
   * The clip itself, for footage only.
   *
   * Separate from `url` because they are not interchangeable: `url` is a still an `img` can draw,
   * and a video element pointed at it would play nothing. A tile with one and not the other is the
   * normal state while the batch resolves.
   */
  videoUrl?: string;
  /** What the library records about this picture. Absent on a tile a caller synthesised. */
  tags?: Record<string, string[]>;
  /**
   * What the library says this is, and what file it is. Both absent on a tile a caller
   * synthesised, which has no library row to have been catalogued from.
   *
   * Carried because a picker has to know what a slot can actually take: `kind` separates a logo
   * from a photograph, and `mimeType` separates an SVG from a raster, which is what decides
   * whether an atom-sourced slot can render it at all.
   */
  kind?: string;
  mimeType?: string | null;
  /**
   * Where ingestion has got to with this row, for a surface that wants to say so.
   *
   * Absent both when the row is catalogued and when the caller asked for no clock, which is why a
   * tile in the canvas picker carries nothing: a badge belongs where somebody just added the file,
   * not where they are choosing between finished ones.
   */
  ingestion?: IngestionState;
  /**
   * When the row was added, as the wire carries it. Absent on a tile a caller synthesised, which has
   * no library row to have been added at.
   */
  createdAt?: string;
}

/**
 * The asset a thumbnail can actually draw, or null when there is not one yet.
 *
 * A clip's own asset is the video file, which an image element cannot draw, so footage resolves to
 * its still and never falls back to the file: pointing an `img` at an mp4 renders the browser's
 * broken-image icon, which reads as a failed upload rather than as a still that has not been drawn
 * yet. Null instead, so the tile draws nothing and its play badge is what identifies it.
 *
 * A photograph still falls back to its own asset, which is the thing it draws.
 *
 * One rule for the url batch and the tile, because two would disagree about which asset a thumbnail
 * is showing.
 */
const thumbnailIdOf = (row: BrandMediaItem): string | null =>
  row.thumbnailAssetId ?? row.posterAssetId ?? (row.mediaType === 'video' ? null : row.assetId);

/**
 * How long a clip is given to have its still drawn before the library stops expecting one.
 *
 * Extraction happens behind the upload, so a freshly added clip legitimately has no still for a few
 * seconds. Bounded rather than open-ended: a clip whose extraction failed outright would otherwise
 * be polled for as long as the page stays open.
 */
const POSTER_GRACE_MS = 10 * 60 * 1000;

/**
 * Whether any row is a clip still waiting for the still being drawn behind its upload.
 *
 * What a caller polls on. The window closes on its own -- either the poster lands, or the row ages
 * past the grace period -- so nothing has to decide when to stop asking.
 */
export const awaitsPoster = (rows: readonly BrandMediaItem[], now: number): boolean =>
  rows.some(
    (row) =>
      row.mediaType === 'video' &&
      thumbnailIdOf(row) === null &&
      now - new Date(row.createdAt).getTime() < POSTER_GRACE_MS,
  );

/**
 * The tag key a finished description always carries.
 *
 * `adUsage` is the only model-owned key the describer's schema makes mandatory. Mirrors the
 * server's own `needingIngestion` marker in
 * `apps/guidelines-api/src/dao/repository/brand-media.repository.ts`; keep the two in step by hand.
 */
const CATALOGUED_TAG_KEY = 'adUsage';

/**
 * How long a row is given to be catalogued before the library stops expecting it.
 *
 * Minutes rather than the seconds one description takes: describing runs behind a per-model
 * concurrency cap, so the last file of an imported pack legitimately waits for the ones ahead of it.
 */
const INGESTION_GRACE_MS = 10 * 60 * 1000;

/**
 * Where ingestion has got to: still working, or long enough ago that nobody should keep waiting.
 *
 * One state rather than one per field, because one model call writes the tags, both descriptions and
 * the rest of the classification together, so two states would only ever disagree by being wrong.
 *
 * Catalogued means both marks are present, and needs both because either alone can be there before
 * ingestion has run. An upload states its tags to satisfy an image layer's rule, so `adUsage` can
 * be on a row nothing has read yet; a promoted proposal arrives carrying `literalDescription`.
 * Reading one mark alone reports such a row as finished, which costs it the spinner that says it is
 * being worked on and stops the poll that would fetch its thumbnail.
 *
 * `unfinished` rather than `failed`, because from here the two are indistinguishable -- a describer
 * that refused the file and one still queued behind a hundred others look exactly the same on the
 * wire. What the surface can honestly offer either way is a retry.
 */
export type IngestionState = 'pending' | 'unfinished';

/**
 * A row's tags, read the way the detail panel reads them: the type says they are always there and a
 * locally running API built before the field existed sends nothing.
 */
const tagsOf = (tags: Record<string, string[]> | undefined): Record<string, string[]> => tags ?? {};

export const ingestionStateOf = (row: BrandMediaItem, now: number): IngestionState | undefined => {
  if (CATALOGUED_TAG_KEY in tagsOf(row.tags) && row.literalDescription !== null) {
    return undefined;
  }
  return now - new Date(row.createdAt).getTime() < INGESTION_GRACE_MS ? 'pending' : 'unfinished';
};

/**
 * Whether any row is still expected to be catalogued.
 *
 * What a caller polls on, and it closes on its own the same way `awaitsPoster` does: either the tags
 * land, or the row ages out of the grace period. Without it the tags a describer writes seconds
 * after an upload are invisible until something else happens to refetch, which reads as an asset
 * that was never tagged at all.
 */
export const awaitsIngestion = (rows: readonly BrandMediaItem[], now: number): boolean =>
  rows.some((row) => ingestionStateOf(row, now) === 'pending');

/**
 * Every asset id a set of rows needs a signed url for.
 *
 * Footage asks for two: the poster it draws and the clip it plays. Signing a url a grid may never
 * use is cheap and batched, and the alternative is resolving on hover, which spends a round trip
 * exactly when someone is waiting to see something move. Bytes are not the cost here; nothing
 * downloads until a video element is mounted.
 */
export const mediaUrlIds = (rows: readonly BrandMediaItem[]): string[] =>
  rows.flatMap((row) => {
    const thumbnail = thumbnailIdOf(row);
    const wanted = row.mediaType === 'video' ? [thumbnail, row.assetId] : [thumbnail];
    // A clip with no still yet contributes only its own file, which is what the hover preview plays.
    return wanted.filter((id): id is string => id !== null);
  });

/**
 * One row against a resolved url batch. Absent urls stay absent rather than becoming empty strings.
 *
 * `now` is passed in rather than read here so a tile is a pure function of its inputs: a caller that
 * omits it gets no ingestion state, which is what the canvas picker wants.
 */
export const toMediaTile = (
  row: BrandMediaItem,
  urls: ResolvedUrls | undefined,
  now?: number,
): MediaTile => {
  const thumbnail = thumbnailIdOf(row);
  const ingestion = now === undefined ? undefined : ingestionStateOf(row, now);

  return {
    id: row.id,
    assetId: row.assetId,
    title: row.title,
    mediaType: row.mediaType,
    description: row.literalDescription ?? undefined,
    url: thumbnail === null ? undefined : urls?.[thumbnail],
    videoUrl: row.mediaType === 'video' ? urls?.[row.assetId] : undefined,
    tags: row.tags,
    kind: row.kind,
    mimeType: row.mimeType,
    createdAt: row.createdAt,
    ...(ingestion === undefined ? {} : { ingestion }),
  };
};
